import Grid from '@mui/material/Grid';
import UserView from '@features/workspace/components/users/view';
import {
  selectActiveCompanyId,
  selectPrimaryUser,
  selectUsersData,
  selectUsersDataFilterAuthPerson,
  selectUsersDataFilterDirector,
} from '@features/workspace/store/selectors';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { Box } from '@mui/material';
import {
  ActionLinkEdit,
  ActionLinkRemove,
} from '@features/workspace/components/controls/action-link';
import { makeStyles } from 'tss-react/mui';
import { useMemo, useState } from 'react';
import { setNotification } from '@store/features/notification';
import { setIsLoading } from '@store/features/loading';
import { unassignEmployee } from '@features/workspace/store/actions';
import { setRemoveUser } from '@features/workspace/store';
import ConfirmDialog from '@features/workspace/components/dialogs/confirm-dialog';
import { UserManagementMode } from '@features/workspace/components/users/utils/constants';
import { GENERAL_ERROR_MESSAGE } from '@utils/constants';
import { IRootState } from '@app/store';
import { IUserData } from '../../types';
import { getValueByUserType } from './utils/user-management';

const useStyles = makeStyles<any>()({
  withDivider: {
    position: 'relative',
    borderBottom: '1px solid #0000001f',
    '&:nth-last-of-type(-n+2)': {
      borderBottom: 'none',
    },
    '&:nth-of-type(odd)::after': {
      display: 'block',
      content: '""',
      position: 'absolute',
      right: 0,
      top: '25%',
      height: '50%',
      borderRight: '1px solid #0000001f',
    },
  },
});

interface IProps {
  type?: UserManagementMode;
  getEditUrl: (id: string) => string;
  bankingMode?: boolean;
}

export function Users({ type, getEditUrl, bankingMode = false }: IProps) {
  const { classes } = useStyles(null);
  const dispatch = useAppDispatch();

  const [removeUserId, setRemoveUserId] = useState<null | string>(null);
  const companyId = useAppSelector(selectActiveCompanyId);
  const primaryUser = useAppSelector(selectPrimaryUser);

  const usersSelector = getValueByUserType<{
    fallback: (state: IRootState) => IUserData[];
    director: (state: IRootState) => IUserData[];
    nonDirector: (state: IRootState) => IUserData[];
  }>(type, {
    fallback: selectUsersData,
    director: selectUsersDataFilterDirector,
    nonDirector: selectUsersDataFilterAuthPerson,
  });

  const users = useAppSelector(usersSelector);

  const removeUserName = useMemo(() => {
    const user = users?.find((u: any) => u.id === removeUserId);

    return [user?.person?.firstName, user?.person?.lastName].join(' ');
  }, [users, removeUserId]);

  const onStartDelete = (userId: string) => () => setRemoveUserId(userId);

  const onDeleteConfirm = async () => {
    dispatch(setIsLoading(true));

    const user = users.find((u) => u.id === removeUserId);

    if (user) {
      if (primaryUser.id === user.id) {
        const messageSpecification = getValueByUserType<{
          fallback: string;
          director: string;
          nonDirector: string;
        }>(type, {
          fallback: 'user',
          director: 'director',
          nonDirector: 'auth person',
        });

        dispatch(
          setNotification({
            message: `Cannot remove ${messageSpecification} who is the company primary user`,
            variant: 'warning',
          })
        );
      } else {
        try {
          await dispatch(
            unassignEmployee({
              companyId,
              userId: removeUserId,
            })
          );

          const messageSpecification = getValueByUserType<{
            fallback: string;
            director: string;
            nonDirector: string;
          }>(type, {
            fallback: 'User',
            director: 'Director',
            nonDirector: 'Auth person',
          });

          dispatch(setRemoveUser(removeUserId));
          dispatch(
            setNotification({
              message: `${messageSpecification} ${removeUserName} has been removed`,
              variant: 'success',
            })
          );
        } catch (err) {
          dispatch(
            setNotification({
              message: GENERAL_ERROR_MESSAGE,
              variant: 'error',
            })
          );
        }
      }
    }

    setRemoveUserId(null);
    dispatch(setIsLoading(false));
  };

  const onCancel = async () => {
    setRemoveUserId(null);
  };

  return (
    <>
      <Grid container spacing={8}>
        {users.map((user: any) => (
          <Grid
            key={user.id}
            item
            className={classes.withDivider}
            md={6}
            xs={12}
          >
            <Box display="flex" flexDirection="column">
              <Box display="flex">
                <UserView bankingMode={bankingMode} user={user} />
              </Box>
              <Box
                display="flex"
                justifyContent="flex-end"
                pb={5}
                pr={5}
                py={2.5}
              >
                <Box display="flex">
                  <ActionLinkEdit uri={getEditUrl(user.id)}>
                    Edit
                  </ActionLinkEdit>
                </Box>
                <Box display="flex" ml={2}>
                  <ActionLinkRemove onClick={onStartDelete(user.id)}>
                    Remove
                  </ActionLinkRemove>
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <ConfirmDialog
        cancelButtonText="Cancel"
        confirmButtonText="Confirm"
        content={`Are you sure you want to remove ${removeUserName}?`}
        open={!!removeUserId}
        onCancel={() => onCancel()}
        onClose={() => onCancel()}
        onConfirm={() => onDeleteConfirm()}
      />
    </>
  );
}

export default Users;
